import React from "react"
import {
  Card,
  CardBody,
  Col,
  Row,
  Table,
  UncontrolledTooltip,
} from "reactstrap"
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { useSelector } from "react-redux"
import { useEffect } from "react"
import {
  deleteCategory,
  getAllCategoriesList,
  updateCategoryStatus,
} from "store/actions/categoriesAction"
import { useState } from "react"
import Switch from "components/Shared/Switch"
import DeleteModal from "components/Common/DeleteModal"
import Filters from "components/Common/Filters"
import Paginations from "components/Common/Paginations"
import { showAlert } from "store/reducers/alertReducer"
import DataExportDropdown from "components/Shared/DataExportDropdown"
import isPermissionEnabled, {
  getSubPermissions,
} from "common/isPermissionEnabled"
import placeholderImage from "assets/images/placeholderImage.png"
import { pdfDownload } from "helpers/api_helper"
import DownloadXLSX from "components/Shared/DownloadXLSX"
import DownloadCSV from "components/Shared/DownloadCSV"
import { EXPORT_DATA } from "helpers/url_helper"
import TableShimmer from "components/Shared/TableShimmer"
import { getCountryList } from "store/actions/countriesAction"
import { useTranslation } from "react-i18next"

const Categories = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t("Categories List | Sign Burger")

  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const pageNumber = parseInt(searchParams.get("page")) || 1
  const [selectedId, setSelectedId] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [itemIdToDelete, setItemIdToDelete] = useState(null)
  const [isApiCalled, setIsApiCalled] = useState(false)

  const navigate = useNavigate()
  const dispatch = useDispatch()

  const {
    loading,
    isLoading,
    isUpdating,
    totalCount,
    itemsPerPage,
    currentPage,
    totalPages,
    categoriesLists,
  } = useSelector(state => state.Categories)
  const { countryList } = useSelector(state => state.Countries)

  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "")
  const [selectedFilters, setSelectedFilters] = useState({
    status: parseInt(searchParams.get("status") || "2"),
    sortOrder: parseInt(searchParams.get("sortOrder") || "1"),
    categoryType: searchParams.get("categoryType") || "all",
    countryCode: [],
  })

  const [usersPermissions, setUsersPermissions] = useState({
    list: null,
    create: null,
    edit: null,
    delete: null,
  })

  const { userRole, accountType } = useSelector(state => state.Login)

  // FIRST CHECK PERMISSION, IF DOES'T HAVE THEN REDIRECT TO DASHBOARD
  useEffect(() => {
    const isPermissionList = getSubPermissions(
      userRole,
      4,
      "menus_1",
      accountType
    )
    const isPermissionView = isPermissionEnabled(
      userRole,
      4,
      "menus_1",
      accountType
    )

    const isPermissionCreate = isPermissionEnabled(
      userRole,
      4,
      "menus_2",
      accountType
    )
    const isPermissionEdit = getSubPermissions(
      userRole,
      4,
      "menus_3",
      accountType
    )
    const isPermissionDelete = isPermissionEnabled(
      userRole,
      4,
      "menus_4",
      accountType
    )
    if (
      (accountType !== "superadmin" &&
        !isPermissionList?.menu_full_access_view?.isEnabled) ||
      !isPermissionView
    ) {
      navigate("/dashboard")
    } else {
      getAllCategories()
    }

    setUsersPermissions({
      list: isPermissionList,
      create: isPermissionCreate,
      edit: isPermissionEdit,
      delete: isPermissionDelete,
    })
  }, [userRole])

  useEffect(() => {
    fetchCountryList()
  }, [])

  const getIsComboFilter = categoryType => {
    if (categoryType === "combo") return true
    if (categoryType === "non_combo") return false
    return undefined
  }

  const getAllCategories = () => {
    const { status, sortOrder, categoryType } = selectedFilters
    const isCombo = getIsComboFilter(categoryType)
    const payload = {
      perPage: itemsPerPage,
      pageNo: pageNumber,
      status: status,
      orderBy: sortOrder,
      keyword: searchTerm,
      ...(isCombo !== undefined && { isCombo }),
    }
    dispatch(getAllCategoriesList(payload))
    setIsApiCalled(true)
  }

  const fetchCountryList = () => {
    dispatch(getCountryList())
  }

  const filters = [
    {
      name: "status",
      type: "select",
      options: [
        { label: t("All"), value: 2 },
        { label: t("Active"), value: 1 },
        { label: t("Deactive"), value: 0 },
      ],
      placeholder: t("All"),
    },
    {
      name: "sortOrder",
      type: "select",
      options: [
        { label: t("Ascending"), value: 1 },
        { label: t("Descending"), value: 2 },
      ],
      placeholder: t("Ascending"),
    },
    {
      name: "categoryType",
      type: "select",
      options: [
        { label: t("All"), value: "all" },
        { label: t("Combo Category"), value: "combo" },
        { label: t("Non Combo Category"), value: "non_combo" },
      ],
      placeholder: t("All"),
    },
    {
      name: "countryCode",
      type: "multiSelect",
      options: countryList?.map(item => ({
        label: language === "sa" ? item.nameAr : item.name,
        value: item.countryCode,
      })),

      placeholder: t("Select Countries"),
    },
  ]

  const handleStatusClick = (id, status) => {
    const payload = {
      id,
      status: status === 1 ? "0" : "1", //deactive 1 active
    }
    dispatch(updateCategoryStatus(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
    setSelectedId(id)
  }

  const handleDelete = () => {
    const payload = {
      id: itemIdToDelete,
      status: "2", // for delete
    }
    dispatch(deleteCategory({ ...payload, setShowDeleteModal }))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
  }

  const handleSearchChange = e => {
    setSearchTerm(e.target.value)
  }

  const handleSearch = () => {
    getApiCall(1)
  }

  // useEffect(() => {
  //   if (isApiCalled) {
  //     if (searchTerm) {
  //       const delayDebounceFn = setTimeout(() => {
  //         handleSearch()
  //       }, 500)

  //       return () => clearTimeout(delayDebounceFn)
  //     } else {
  //       handleSearch()
  //     }
  //   }
  // }, [searchTerm, selectedFilters])

  const handleFilterChange = (selectedValue, filterName) => {
    setSelectedFilters(prevFilters => ({
      ...prevFilters,
      [filterName]: selectedValue,
    }))
  }

  const handleReset = () => {
    setSearchTerm("")
    const payload = {
      perPage: 10,
      pageNo: 1,
      status: 2,
      orderBy: 1,
      keyword: "",
    }
    dispatch(getAllCategoriesList(payload))
    setSelectedFilters({
      status: 2,
      sortOrder: 1,
      categoryType: "all",
      countryCode: [],
    })
    setSearchTerm("")
    navigate(`/menus/categories`)
  }

  const onPageChange = page => {
    getApiCall(page)
  }

  const getApiCall = (page = 1) => {
    const { status, sortOrder, countryCode, categoryType } = selectedFilters

    const updatedCountryCode = countryCode?.map(item => item.value) || []
    const isCombo = getIsComboFilter(categoryType)

    const searchParams = new URLSearchParams()
    searchTerm && searchParams.set("search", searchTerm)
    searchParams.set("status", status.toString())
    searchParams.set("sortOrder", sortOrder.toString())
    searchParams.set("categoryType", categoryType)
    searchParams.set("page", page.toString())

    const payload = {
      perPage: itemsPerPage,
      pageNo: page,
      status: Number(status),
      orderBy: Number(sortOrder),
      keyword: searchTerm || "",
      countryCode: updatedCountryCode,
      ...(isCombo !== undefined && { isCombo }),
    }

    dispatch(getAllCategoriesList(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          navigate(`/menus/categories?${searchParams.toString()}`)
        }
      })
      .catch(error => {
        console.error("Error fetching categories:", error)
      })
  }

  const handleExcelDownload = async () => {
    const { status, countryCode, categoryType } = selectedFilters
    const updatedCountryCode = countryCode?.map(item => item.value)
    const isCombo = getIsComboFilter(categoryType)

    const payload = {
      status: Number(status),
      keyword: searchTerm || "",
      type: "CATEGORY",
      exportType: "xlsx",
      countryCode: updatedCountryCode,
      ...(isCombo !== undefined && { isCombo }),
    }
    const response = await pdfDownload(EXPORT_DATA, payload)
    if (response) {
      const xlsxData = response
      DownloadXLSX(xlsxData, t("Categories"))
    }
  }

  const exportToCSV = async () => {
    const { status, countryCode, categoryType } = selectedFilters
    const updatedCountryCode = countryCode?.map(item => item.value)
    const isCombo = getIsComboFilter(categoryType)

    const payload = {
      status: Number(status),
      keyword: searchTerm || "",
      type: "CATEGORY",
      exportType: "csv",
      countryCode: updatedCountryCode,
      ...(isCombo !== undefined && { isCombo }),
    }
    const response = await pdfDownload(EXPORT_DATA, payload)
    if (response) {
      const csvData = response
      DownloadCSV(csvData, t("Categories"))
    }
  }

  return (
    <>
      <div className="page-content category_page">
        <div className="container-fluid">
          <Breadcrumbs />
          <Row>
            <Col xl={12}>
              <Card>
                <DataExportDropdown
                  cardTitle={t("Categories List")}
                  isAddBtn={usersPermissions.create}
                  addBtnText={t("Add Category")}
                  onAddBtnClick={() => navigate("/menus/categories/add")}
                  isExportData={loading ? false : true}
                  onDownloadCSV={exportToCSV}
                  onDownloadExcel={handleExcelDownload}
                />
                {/* {!loading && ( */}
                <CardBody>
                  <Filters
                    filters={filters}
                    searchTerm={searchTerm}
                    handleSearchChange={handleSearchChange}
                    handleFilterChange={handleFilterChange}
                    handleSearch={handleSearch}
                    onReset={handleReset}
                    selectedFilters={selectedFilters}
                    searchBy={t("Name")}
                    colSize={4}
                  />
                </CardBody>
                {/* )} */}
              </Card>
            </Col>
          </Row>
          <Row className="m-auto">
            {loading ? (
              <Card>
                <TableShimmer row={7} col={3} />
              </Card>
            ) : categoriesLists.length === 0 ? (
              <p className="my-3 text-center">{t("No categories found")}</p>
            ) : (
              <Card className="p-0">
                <CardBody className="p-0">
                  <Table className="table mb-0 " hover>
                    <thead className="table-light">
                      <tr>
                        <th className="text-start min_80">
                          <span className="d-inline-block py-2 px-2">
                            {t("Categories")}
                          </span>
                        </th>
                        <th className="text-start">
                          {(accountType === "superadmin" ||
                            usersPermissions?.edit?.menu_full_access_update
                              ?.isEnabled) && (
                            <span className="d-inline-block py-2">
                              {t("Status")}
                            </span>
                          )}
                        </th>
                        <th className="text-end">
                          <span
                            className="d-inline-block py-2"
                            style={{
                              paddingRight: 20,
                              display: "block",
                            }}
                          >
                            {t("Action")}
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {categoriesLists.map(
                        ({ _id, name, nameAr, imageUrl, status, isCombo }) => (
                          <tr key={_id}>
                            <td className="text-start">
                              <div className="left_section ps-2">
                                <Link
                                  to={
                                    isCombo
                                      ? `/menus/combos/${_id}/items`
                                      : `/menus/categories/${_id}/items`
                                  }
                                >
                                  <div className="d-flex align-items-center">
                                    <img
                                      width={47}
                                      height={44}
                                      src={
                                        imageUrl ? imageUrl : placeholderImage
                                      }
                                      style={{ objectFit: "contain" }}
                                    />
                                    <h6
                                      className="cat_title in_line_one"
                                      style={{ margin: "0 16px" }}
                                    >
                                      {language === "sa"
                                        ? nameAr || "-"
                                        : name || "-"}
                                      {isCombo && (
                                        <span
                                          className="badge bg-info ms-2"
                                          style={{ fontSize: "10px" }}
                                        >
                                          {t("Combo")}
                                        </span>
                                      )}
                                    </h6>
                                  </div>
                                </Link>
                              </div>
                            </td>
                            <td className="text-start">
                              {(accountType === "superadmin" ||
                                usersPermissions?.edit?.menu_full_access_update
                                  ?.isEnabled) && (
                                <div style={{ marginTop: 10 }}>
                                  <Switch
                                    isChecked={status}
                                    disabled={_id === selectedId && isUpdating}
                                    onClickHandler={() =>
                                      handleStatusClick(_id, status)
                                    }
                                  />
                                </div>
                              )}
                            </td>
                            <td className="text-end">
                              <div
                                className="d-flex justify-content-end align-items-center pe-2"
                                style={{ marginTop: 10 }}
                              >
                                {(accountType === "superadmin" ||
                                  usersPermissions?.edit
                                    ?.menu_full_access_update?.isEnabled) && (
                                  <>
                                    <i
                                      id={`edittooltip_${_id}`}
                                      onClick={() => {
                                        navigate(
                                          `/menus/categories/edit?id=${_id}`
                                        )
                                      }}
                                      className="mdi mdi-pencil font-size-18 text-success mx-2 cursor-pointer"
                                    ></i>
                                    <UncontrolledTooltip
                                      placement="top"
                                      target={`edittooltip_${_id}`}
                                    >
                                      {t("Edit")}
                                    </UncontrolledTooltip>
                                  </>
                                )}
                                {usersPermissions.list && (
                                  <>
                                    <i
                                      id={`viewtooltip_${_id}`}
                                      onClick={() => {
                                        navigate(
                                          `/menus/categories/category-details?id=${_id}`
                                        )
                                      }}
                                      className="mdi mdi-eye font-size-18 text-black mx-2 cursor-pointer"
                                    ></i>
                                    <UncontrolledTooltip
                                      placement="top"
                                      target={`viewtooltip_${_id}`}
                                    >
                                      {t("View")}
                                    </UncontrolledTooltip>
                                  </>
                                )}
                                {usersPermissions.delete && (
                                  <>
                                    <i
                                      id={`deletetooltip_${_id}`}
                                      onClick={e => {
                                        setItemIdToDelete(_id)
                                        setShowDeleteModal(true)
                                      }}
                                      className="bx bxs-trash font-size-18 mx-2 cursor-pointer"
                                      style={{ color: "#FD5F62" }}
                                    ></i>
                                    <UncontrolledTooltip
                                      placement="top"
                                      target={`deletetooltip_${_id}`}
                                    >
                                      {t("Delete")}
                                    </UncontrolledTooltip>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </Table>
                </CardBody>
              </Card>
            )}
            {/* <Col xl={2} sm={6} key={_id}>
                   <Card className="full_height_card">
                    <CardBody>
                       <Link to={`/menus/categories/${_id}/items`}>
                         <img
                          src={imageUrl ? imageUrl : placeholderImage}
                          className="category_img item_image"
                        />
                      </Link>
                      <div className="inner d-flex align-items-center justify-content-between mt-3">
                        <Link
                          to={`/menus/categories/${_id}/items`}
                          className="title_a"
                        >
                          <h5 className="title in_2_line fs-6">{name}</h5>
                        </Link>
                        <div className="d-flex">
                          {usersPermissions.edit && (
                            <Switch
                              isChecked={status}
                              disabled={_id === selectedId && isUpdating}
                              onClickHandler={() =>
                                handleStatusClick(_id, status)
                              }
                            />
                          )}
                          <UncontrolledDropdown>
                            <DropdownToggle tag="a" className="card-drop">
                              <i className="mdi mdi-dots-vertical font-size-18"></i>
                            </DropdownToggle>
                            <DropdownMenu className="dropdown-menu-end">
                              {usersPermissions.edit && (
                                <DropdownItem
                                  onClick={() => {
                                    navigate(`/menus/categories/edit?id=${_id}`)
                                  }}
                                >
                                  <i className="mdi mdi-pencil font-size-16 text-success me-1"></i>
                                  Edit
                                </DropdownItem>
                              )}
                              {usersPermissions.list && (
                                <DropdownItem
                                  onClick={() => {
                                    navigate(
                                      `/menus/categories/category-details?id=${_id}`
                                    )
                                  }}
                                >
                                  <i className="mdi mdi-eye font-size-16 text-primary me-1"></i>
                                  View
                                </DropdownItem>
                              )}
                              {usersPermissions.delete && (
                                <DropdownItem
                                  onClick={e => {
                                    setItemIdToDelete(_id)
                                    setShowDeleteModal(true)
                                  }}
                                >
                                  <i className="mdi mdi-trash-can font-size-16 text-danger me-1"></i>
                                  Delete
                                </DropdownItem>
                              )}
                            </DropdownMenu>
                          </UncontrolledDropdown>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
               ))
            )} */}
            {!loading && (
              <Card>
                <CardBody>
                  <Paginations
                    data={categoriesLists}
                    itemsPerPage={itemsPerPage}
                    onPageChange={onPageChange}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalCount={totalCount}
                  />
                </CardBody>
              </Card>
            )}
            <div className="mb-3"></div>
          </Row>
        </div>
      </div>
      <DeleteModal
        show={showDeleteModal}
        onDeleteClick={handleDelete}
        isLoading={isLoading}
        onCloseClick={() => {
          setItemIdToDelete(null)
          setShowDeleteModal(false)
        }}
        buttonText={t("Delete")}
        bodyTitle={t("Are you sure you want to delete this {{name}}?", {
          name: t("Category"),
        })}
      />
    </>
  )
}

export default Categories
